// N-15b: 通知先 1 人の追加 / 編集モーダル。
// 検証は shared の `validateNotificationRecipients`（Callable と同じ関数）を 1 人分に掛ける。

import { useState, type FormEvent } from 'react';
import {
  ADMIN_NOTIFICATION_CHANNELS,
  ADMIN_NOTIFICATION_KINDS,
  ADMIN_NOTIFICATION_KIND_DESCRIPTIONS,
  ADMIN_NOTIFICATION_KIND_LABELS,
  emptyAdminNotificationKindFlags,
  validateNotificationRecipients,
  type AdminNotificationChannel,
  type AdminNotificationKind,
  type AppUser,
  type NotificationRecipient,
} from '@kawai/shared';
import { Button, Modal, TextField, ZebraTable } from '@kawai/ui';
import type { AllUsersStatus } from '../../lib/hooks/useAllUsers.js';
import { CustomerPicker } from './CustomerPicker.js';

const CHANNEL_LABELS: Record<AdminNotificationChannel, string> = {
  email: 'メール',
  line: 'LINE',
};

export interface RecipientFormProps {
  open: boolean;
  /** 編集対象。undefined なら新規。 */
  initial?: NotificationRecipient;
  users: ReadonlyArray<AppUser>;
  usersStatus: AllUsersStatus;
  onCancel: () => void;
  onSubmit: (recipient: NotificationRecipient) => void;
}

function newId(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  return `r_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function blank(): NotificationRecipient {
  return { id: newId(), name: '', kinds: emptyAdminNotificationKindFlags(), enabled: true };
}

export function RecipientForm({
  open,
  initial,
  users,
  usersStatus,
  onCancel,
  onSubmit,
}: RecipientFormProps) {
  const [draft, setDraft] = useState<NotificationRecipient>(() => initial ?? blank());
  const [errors, setErrors] = useState<string[]>([]);

  const setKind = (kind: AdminNotificationKind, ch: AdminNotificationChannel, on: boolean) =>
    setDraft((d) => ({
      ...d,
      kinds: { ...d.kinds, [kind]: { ...d.kinds[kind], [ch]: on } },
    }));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const email = draft.email?.trim() ?? '';
    const normalized: NotificationRecipient = { ...draft, name: draft.name.trim() };
    if (email) normalized.email = email;
    else delete normalized.email;
    const errs = validateNotificationRecipients([normalized]).map((m) =>
      m.replace(/^1 人目(（[^）]*）)?: /, ''),
    );
    if (errs.length > 0) {
      setErrors(errs);
      return;
    }
    onSubmit(normalized);
  };

  const formId = 'recipient-form';

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={initial ? '通知先を編集' : '通知先を追加'}
      size="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onCancel}>
            キャンセル
          </Button>
          <Button type="submit" form={formId}>
            {initial ? '更新' : '追加'}
          </Button>
        </div>
      }
    >
      <form id={formId} onSubmit={handleSubmit} className="space-y-4" noValidate>
        <TextField
          label="名前"
          value={draft.name}
          onChange={(e) => setDraft((d) => ({ ...d, name: e.target.value }))}
          hint="画面と操作履歴にだけ使います（通知本文には載せません）"
          required
        />
        <TextField
          label="メールアドレス（任意）"
          type="email"
          value={draft.email ?? ''}
          onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
          autoComplete="off"
        />
        <CustomerPicker
          users={users}
          status={usersStatus}
          value={draft.lineUserId ?? null}
          valueLabel={draft.lineDisplayName}
          onChange={(u) =>
            setDraft((d) => {
              const next = { ...d };
              if (u?.lineUserId) {
                next.lineUserId = u.lineUserId;
                next.lineDisplayName = u.displayName;
              } else {
                delete next.lineUserId;
                delete next.lineDisplayName;
              }
              return next;
            })
          }
        />
        <p className="rounded-lg bg-warning-tint px-[18px] py-3 text-label text-warning-ink">
          LINE 宛先に選んだ人には、顧客名を含む社内向け通知が届きます。スタッフ以外を選ばないでください。
        </p>

        {/* legend は枠位置に浮くので section + h3 にする（S-135 の判断） */}
        <section aria-labelledby="recipient-kinds-heading">
          <h3 id="recipient-kinds-heading" className="text-label font-medium text-ink-2">
            受け取る通知
          </h3>
          <div className="mt-2 overflow-x-auto">
            <ZebraTable className="w-full" aria-label="受け取る通知">
              <thead>
                <ZebraTable.HeadRow>
                  <ZebraTable.Th edge="first">種類</ZebraTable.Th>
                  {ADMIN_NOTIFICATION_CHANNELS.map((ch, i) => (
                    <ZebraTable.Th
                      key={ch}
                      align="center"
                      edge={i === ADMIN_NOTIFICATION_CHANNELS.length - 1 ? 'last' : undefined}
                    >
                      {CHANNEL_LABELS[ch]}
                    </ZebraTable.Th>
                  ))}
                </ZebraTable.HeadRow>
              </thead>
              <tbody>
                {ADMIN_NOTIFICATION_KINDS.map((kind) => (
                  <ZebraTable.Row key={kind}>
                    <ZebraTable.Td>
                      <div className="text-ink">{ADMIN_NOTIFICATION_KIND_LABELS[kind]}</div>
                      <div className="text-note text-ink-3">
                        {ADMIN_NOTIFICATION_KIND_DESCRIPTIONS[kind]}
                      </div>
                    </ZebraTable.Td>
                    {ADMIN_NOTIFICATION_CHANNELS.map((ch) => (
                      <ZebraTable.Td key={ch} align="center">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-brand"
                          aria-label={`${ADMIN_NOTIFICATION_KIND_LABELS[kind]} を ${CHANNEL_LABELS[ch]} で受け取る`}
                          checked={draft.kinds[kind][ch]}
                          onChange={(e) => setKind(kind, ch, e.target.checked)}
                        />
                      </ZebraTable.Td>
                    ))}
                  </ZebraTable.Row>
                ))}
              </tbody>
            </ZebraTable>
          </div>
        </section>

        <label className="flex items-center gap-2 text-body text-ink">
          <input
            type="checkbox"
            className="h-4 w-4 accent-brand"
            checked={draft.enabled}
            onChange={(e) => setDraft((d) => ({ ...d, enabled: e.target.checked }))}
          />
          有効（OFF にすると全通知から外れます）
        </label>

        {errors.length > 0 && (
          <ul role="alert" className="list-disc pl-5 text-body text-danger-ink">
            {errors.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        )}
      </form>
    </Modal>
  );
}
